import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { PILLAR_LABELS } from './uavsMeta';
import { useTokens } from '../primitives';

const CATEGORY_LABELS = {
  schema: 'Schema',
  robots: 'robots.txt',
  llms: 'llms.txt',
  sitemap: 'Sitemap',
  performance: 'Performance',
  'ai-mention': 'AI mention',
  content: 'Content',
  trust: 'Trust',
  backlink: 'Backlink',
  gbp: 'Google Business',
  'ai-feed': 'AI feed',
  other: 'Audit',
};

const ATTRIBUTION_LABELS = {
  upryzze: 'Upryzze',
  customer: 'You',
  external: 'Site change',
};

function attributionColors(t) {
  return {
    upryzze: { bg: t.greenBg, fg: t.greenText },
    customer: { bg: t.blueBg, fg: t.blue },
    external: { bg: t.grayBg, fg: t.grayText },
  };
}

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

function DirectionIcon({ event }) {
  const t = useTokens();
  const total = event.observedDelta?.totalScore;
  const title = (event.title || '').toLowerCase();
  if (
    title.includes('now passing') ||
    title.includes('improved') ||
    (total ?? 0) > 0
  ) {
    return <TrendingUpIcon sx={{ fontSize: 18, color: t.greenText }} />;
  }
  if (
    title.includes('now failing') ||
    title.includes('regressed') ||
    (total ?? 0) < 0
  ) {
    return <TrendingDownIcon sx={{ fontSize: 18, color: t.red }} />;
  }
  return <TrendingFlatIcon sx={{ fontSize: 18, color: t.muted }} />;
}

function AttributionBadge({ attributedTo }) {
  const t = useTokens();
  const colors = attributionColors(t);
  const c = colors[attributedTo] || colors.external;
  return (
    <Chip
      label={ATTRIBUTION_LABELS[attributedTo] || attributedTo}
      size="small"
      sx={{
        height: 20,
        fontSize: 11,
        bgcolor: c.bg,
        color: c.fg,
        fontWeight: 600,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

function EventCard({ event }) {
  const t = useTokens();
  const pillarLabel = event.pillarsImpacted?.[0]
    ? PILLAR_LABELS[event.pillarsImpacted[0]]
    : null;
  // Phase 1 deliberately never says "this caused X" — we report the window
  // delta when the event is isolated, and leave attribution open otherwise.
  // Copy here mirrors that stance: "alongside this change, your X pillar
  // moved by Y" rather than "this change moved X by Y."
  const isolatedDelta = event.isolated && event.observedDelta?.pillars;
  const isolatedPillarKey = isolatedDelta
    ? Object.keys(isolatedDelta)[0]
    : null;
  const isolatedPillarValue = isolatedPillarKey
    ? isolatedDelta[isolatedPillarKey]
    : null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.75,
        mb: 1,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{ pt: 0.25 }}>
          <DirectionIcon event={event} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {event.title}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
            >
              {formatDate(event.occurredAt)}
            </Typography>
          </Stack>
          {event.description && (
            <Typography variant="body2" sx={{ color: 'text.primary', mt: 0.5 }}>
              {event.description}
            </Typography>
          )}
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ mt: 0.75 }}
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              label={CATEGORY_LABELS[event.category] || event.category}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: 11 }}
            />
            {pillarLabel && (
              <Chip
                label={pillarLabel}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
            <AttributionBadge attributedTo={event.attributedTo} />
          </Stack>
          {isolatedPillarValue != null && isolatedPillarValue !== 0 && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}
            >
              Alongside this change,{' '}
              {PILLAR_LABELS[isolatedPillarKey] || isolatedPillarKey} moved{' '}
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  color: isolatedPillarValue > 0 ? t.greenText : t.red,
                }}
              >
                {isolatedPillarValue > 0
                  ? `+${isolatedPillarValue}`
                  : isolatedPillarValue}
              </Box>{' '}
              points.
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

export default function EventTimeline({ events }) {
  if (!events) return null;
  const list = events.slice(0, 25);

  if (list.length === 0) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          What&apos;s changed
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Once we run a second audit we&apos;ll log every check that flipped
            here — schema additions, robots.txt updates, AI-mention shifts, and
            grade changes — with the date and pillar each one touched.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          What&apos;s changed
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {list.length} {list.length === 1 ? 'event' : 'events'} · most recent
          first
        </Typography>
      </Stack>
      {list.map((e) => (
        <EventCard key={e._id} event={e} />
      ))}
    </Box>
  );
}
